<template>
  <EditorContent
    id="editor"
    :editor="editor"
    class="relative w-full text-style-sm text-ellipsis overflow-hidden"
  />
</template>

<script setup>
import { onUnmounted } from "vue";
import { EditorContent, useEditor } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import mention from "@/SharedComponents/CommentBox/mention";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const emit = defineEmits(["focus", "update:contents"]);

const props = defineProps({
  content: { type: String, default: "" },
  mentionables: { type: Array, default: () => [] },
});

const editor = useEditor({
  extensions: [
    StarterKit,
    Placeholder.configure({
      placeholder: t("components.inputs.comment.placeholder"),
      emptyEditorClass:
        "cursor-text before:content-[attr(data-placeholder)] before:absolute before:text-gray-500 before-pointer-events-none whitespace-nowrap",
    }),
    Mention.configure({
      HTMLAttributes: {
        class:
          "before:content-['@'] before:text-gray-800 before:font-light font-semibold text-brand-800 inline-block",
      },
      suggestion: {
        ...mention,
        allowSpaces: true,
        items: ({ query }) => {
          return props.mentionables
            .filter((user) =>
              user.fullname.toLowerCase().includes(query.toLowerCase())
            )
            .slice(0, 10);
        },
      },
      renderLabel({ node }) {
        return `${node.attrs.label ?? node.attrs.id}`;
      },
    }),
  ],
  editorProps: {
    attributes: {
      class:
        "border-transparent focus:border-transparent focus:ring-0 outline-none text-style-sm",
    },
  },
  onFocus: () => {
    emit("focus");
  },
  onUpdate: ({ editor }) => {
    if (editor.isEmpty) {
      emit("update:contents", {});
    } else {
      emit("update:contents", editor.getJSON());
    }
  },
});

onUnmounted(() => {
  editor.value.destroy();
});

defineExpose({
  clear: () => editor.value.commands.clearContent(),
});
</script>
